import { setSearchCountryScope } from '@/lib/countryScope';
import { resolveGeoSearchScope } from '@/lib/geoScope';
import { saveUserLocation } from '@/lib/location';

export const PROPERTY_LISTING_PATH = '/list-property/new';

/** Query keys accepted from legacy `/list/geo` and applied on listing page. */
export type ListingGeoQueryParams = {
    country?: string | null;
    city_id?: string | null;
    city_name?: string | null;
    city_lat?: string | null;
    city_lng?: string | null;
    geo?: string | null;
};

export function buildListPropertyRedirectSearch(params: URLSearchParams): string {
    const next = new URLSearchParams();
    next.set('geo', '1');
    for (const key of ['country', 'city_id', 'city_name', 'city_lat', 'city_lng', 'draft'] as const) {
        const value = params.get(key)?.trim();
        if (value) next.set(key, value);
    }
    const qs = next.toString();
    return qs ? `?${qs}` : '?geo=1';
}

export function parseListingGeoQuery(
    searchParams: URLSearchParams | ListingGeoQueryParams,
): {
    countryCode: string;
    cityId?: number;
    cityName?: string;
    cityLat?: number;
    cityLng?: number;
} {
    const get = (key: keyof ListingGeoQueryParams): string | undefined => {
        if (searchParams instanceof URLSearchParams) {
            return searchParams.get(key)?.trim() || undefined;
        }
        const raw = searchParams[key];
        return typeof raw === 'string' ? raw.trim() || undefined : undefined;
    };

    const countryCode = (get('country') || 'IN').toUpperCase();
    const cityIdRaw = get('city_id');
    const cityId = cityIdRaw ? Number(cityIdRaw) : undefined;
    const cityName = get('city_name');
    const cityLatRaw = get('city_lat');
    const cityLngRaw = get('city_lng');
    const cityLat = cityLatRaw ? Number(cityLatRaw) : undefined;
    const cityLng = cityLngRaw ? Number(cityLngRaw) : undefined;

    return {
        countryCode,
        cityId: cityId != null && Number.isFinite(cityId) && cityId > 0 ? cityId : undefined,
        cityName,
        cityLat: cityLat != null && Number.isFinite(cityLat) ? cityLat : undefined,
        cityLng: cityLng != null && Number.isFinite(cityLng) ? cityLng : undefined,
    };
}

/**
 * Persist city/country from listing URL params (e.g. after /list/geo redirect),
 * then remap Hyderabad suburbs via resolveGeoSearchScope.
 */
export async function applyListingGeoQueryParams(
    searchParams: URLSearchParams | ListingGeoQueryParams,
): Promise<boolean> {
    const parsed = parseListingGeoQuery(searchParams);
    if (!parsed.cityId && !parsed.cityName) {
        return false;
    }

    setSearchCountryScope({
        code: parsed.countryCode,
        name: parsed.countryCode === 'IN' ? 'India' : parsed.countryCode,
        source: 'manual',
    });

    if (parsed.cityName || parsed.cityId) {
        saveUserLocation({
            city: parsed.cityName || 'City',
            state: '',
            country: parsed.countryCode === 'IN' ? 'India' : parsed.countryCode,
            countryCode: parsed.countryCode,
            catalog_enabled: true,
            city_id: parsed.cityId,
            lat: parsed.cityLat,
            lon: parsed.cityLng,
        });
    }

    await resolveGeoSearchScope();
    return true;
}
